import React, { useState } from 'react';
import { fmtM } from '../utils';

// ── STAT CARD ──
export function StatCard({ label, value, sub, color, small }) {
  return (
    <div style={{
      background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)',
      padding: small ? '12px 14px' : '16px', flex: 1, minWidth: 0
    }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: small ? 18 : 24, fontWeight: 700, fontFamily: 'Courier, monospace', letterSpacing: -1, color: color || 'var(--tx)' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ── STAT ROW ──
export function StatRow({ stats }) {
  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
      {stats.map((s, i) => <StatCard key={i} {...s} />)}
    </div>
  );
}

// ── BADGE ──
const BADGE_STYLES = {
  grn: { bg: 'var(--grn-l)', color: 'var(--grn)' },
  red: { bg: 'var(--red-l)', color: 'var(--red)' },
  blu: { bg: 'var(--blu-l)', color: 'var(--blu-m)' },
  yel: { bg: 'var(--yel-l)', color: 'var(--yel)' },
  pur: { bg: 'var(--pur-l)', color: 'var(--pur)' },
  gy:  { bg: 'var(--surf3)', color: 'var(--tx2)' },
  ora: { bg: 'var(--ora-l)', color: 'var(--ora)' },
};
export function Badge({ type = 'gy', children }) {
  const s = BADGE_STYLES[type] || BADGE_STYLES.gy;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: s.bg, color: s.color }}>
      {children}
    </span>
  );
}

export function TipoBadge({ tipo }) {
  const map = { venta: 'grn', gasto: 'red', compra: 'ora', retiro: 'pur', pago_proveedor: 'yel', deposito: 'blu', egreso: 'red' };
  const labels = { venta: 'Venta', gasto: 'Gasto', compra: 'Compra', retiro: 'Retiro', pago_proveedor: 'Pago', deposito: 'Depósito', egreso: 'Egreso' };
  return <Badge type={map[tipo] || 'gy'}>{labels[tipo] || tipo}</Badge>;
}

export function MedioBadge({ medio }) {
  const map = { efectivo: 'grn', transferencia: 'blu', qr: 'blu', cuenta_corriente: 'yel' };
  const labels = { efectivo: 'Efectivo', transferencia: 'Cuentas', qr: 'QR (c/com.)', cuenta_corriente: 'Cta Cte' };
  return <Badge type={map[medio] || 'gy'}>{labels[medio] || medio || '-'}</Badge>;
}

// ── BUTTON ──
export function Btn({ children, onClick, variant = 'default', size = 'md', disabled, style }) {
  const variants = {
    default: { bg: 'var(--surf2)', color: 'var(--tx)', border: '1px solid var(--border2)' },
    grn: { bg: 'var(--grn)', color: '#fff', border: '1px solid var(--grn)' },
    red: { bg: 'transparent', color: 'var(--red)', border: '1px solid #d9b0ad' },
    blu: { bg: 'var(--blu)', color: '#fff', border: '1px solid var(--blu)' },
    ghost: { bg: 'transparent', color: 'var(--tx2)', border: '1px solid var(--border)' },
  };
  const sizes = { sm: { padding: '4px 9px', fontSize: 11 }, md: { padding: '7px 13px', fontSize: 12 }, lg: { padding: '10px 18px', fontSize: 13 } };
  const v = variants[variant] || variants.default;
  const sz = sizes[size] || sizes.md;
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...v, ...sz, borderRadius: 'var(--r)', cursor: disabled ? 'default' : 'pointer',
      fontFamily: 'Arial, sans-serif', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5,
      opacity: disabled ? .5 : 1, transition: 'all .12s', ...style
    }}>
      {children}
    </button>
  );
}

// ── TABLE ──
export function Table({ headers, rows, emptyMsg = 'Sin datos', emptyColspan = 5 }) {
  return (
    <div style={{ background: 'var(--surf)', border: '1px solid var(--border)', borderRadius: 'var(--rl)', overflow: 'hidden', overflowX: 'auto', marginBottom: 16 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: 'var(--surf2)' }}>
            {headers.map((h, i) => (
              <th key={i} style={{ textAlign: 'left', padding: '9px 13px', fontSize: 9.5, fontWeight: 700, color: 'var(--tx3)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', letterSpacing: '.07em', textTransform: 'uppercase' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0
            ? <tr><td colSpan={emptyColspan} style={{ textAlign: 'center', padding: 40, color: 'var(--tx3)' }}>{emptyMsg}</td></tr>
            : rows.map((row, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--border)', cursor: row.onClick ? 'pointer' : 'default' }}
                onClick={row.onClick}
                onMouseEnter={e => { if (row.onClick) e.currentTarget.style.background = 'var(--surf2)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                {row.cells.map((cell, j) => (
                  <td key={j} style={{ padding: '9px 13px', color: 'var(--tx)', verticalAlign: 'middle', ...cell.style }}>{cell.content}</td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

// ── MODAL ──
export function Modal({ title, onClose, onSave, saveLabel = 'Guardar', children, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(3px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--surf)', borderRadius: 'var(--rl)', border: '1px solid var(--border2)', padding: 22, width: wide ? 600 : 500, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tx3)', fontSize: 22, lineHeight: 1 }}>×</button>
        </div>
        {children}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
          <Btn onClick={onClose}>Cancelar</Btn>
          {onSave && <Btn variant="grn" onClick={onSave}>{saveLabel}</Btn>}
        </div>
      </div>
    </div>
  );
}

// ── FORM FIELD ──
export function Field({ label, children, full }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, gridColumn: full ? '1/-1' : undefined }}>
      <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--tx2)', letterSpacing: '.05em', textTransform: 'uppercase' }}>{label}</label>
      {children}
    </div>
  );
}

export function Input({ value, onChange, type = 'text', placeholder, autoFocus, style }) {
  return (
    <input value={value} onChange={e => onChange(e.target.value)} type={type} placeholder={placeholder} autoFocus={autoFocus}
      style={{ padding: '8px 10px', borderRadius: 'var(--r)', border: '1px solid var(--border2)', fontSize: 13, fontFamily: 'Arial, sans-serif', background: 'var(--surf)', color: 'var(--tx)', outline: 'none', width: '100%', ...style }} />
  );
}

export function Select({ value, onChange, children, style }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ padding: '8px 10px', borderRadius: 'var(--r)', border: '1px solid var(--border2)', fontSize: 13, fontFamily: 'Arial, sans-serif', background: 'var(--surf)', color: 'var(--tx)', outline: 'none', width: '100%', ...style }}>
      {children}
    </select>
  );
}

export function Textarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
      style={{ padding: '8px 10px', borderRadius: 'var(--r)', border: '1px solid var(--border2)', fontSize: 13, fontFamily: 'Arial, sans-serif', background: 'var(--surf)', color: 'var(--tx)', outline: 'none', width: '100%', resize: 'vertical' }} />
  );
}

export function FormGrid({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>{children}</div>;
}

// ── TOAST ──
let toastTimeout;
export function showToast(msg) {
  const t = document.getElementById('app-toast');
  if (!t) return;
  t.textContent = msg;
  t.style.opacity = 1;
  t.style.transform = 'translateY(0)';
  clearTimeout(toastTimeout);
  toastTimeout = setTimeout(() => { t.style.opacity = 0; t.style.transform = 'translateY(8px)'; }, 2800);
}

// ── ALERT BANNER ──
export function AlertBanner({ type = 'warning', children }) {
  const styles = {
    warning: { bg: 'var(--yel-l)', border: '#d4a843', color: 'var(--yel)' },
    error: { bg: 'var(--red-l)', border: '#d9b0ad', color: 'var(--red)' },
    success: { bg: 'var(--grn-l)', border: '#b0d9be', color: 'var(--grn)' },
    info: { bg: 'var(--blu-l)', border: '#a0c4e0', color: 'var(--blu-m)' },
  };
  const s = styles[type] || styles.warning;
  return (
    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 'var(--rl)', padding: '12px 16px', marginBottom: 14, color: s.color, fontSize: 12 }}>
      {children}
    </div>
  );
}

// ── SECTION HEADER ──
export function SectionHeader({ title, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '.07em' }}>{title}</div>
      {action}
    </div>
  );
}

// ── AVATAR ──
export function Avatar({ name, color, size = 28 }) {
  const initials = (name || '?').split(' ').map(w => w[0] || '').join('').toUpperCase().slice(0, 2);
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: color || '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.35, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
      {initials}
    </div>
  );
}
